import React, { useEffect, useMemo, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand';

import RichardMonogram from '~/pages/AskRichardUnified/chat/RichardMonogram';

import { useInterpretLensFilters } from '~/hooks/apis/reporting/useInterpretLensFilters';
import { useGetLensColumnValues } from '~/hooks/apis/reporting/useLensColumnValues';

import {
  LENS_PERIOD_KEY,
  type LensCalendar,
  type LensDataset,
  type LensFilter,
  type LensFilterValues,
  type LensPeriod,
  type LensResolvedPeriod,
} from '~/types/leLens';

import PeriodRows from './PeriodRows';
import {
  type LensRange,
  type LensSelection,
  distinctValues,
  hasSelection,
  inferDimensions,
  looksLikeDate,
  numericColumns,
  valueExtent,
} from './cardView';
import { declaredDatasetDescription, declaredDatasetLabel, humaniseFilterLabel } from './filterLabels';
import { governedDisplayDataset, useGovernedDisplayMetadata } from './governedDisplayMetadata';
import {
  announceLensFilter,
  describeClearAllAnnouncement,
  describeRangeAnnouncement,
  describeSelectionAnnouncement,
} from './lensAnnouncer';
import {
  type BoardFilterProposal,
  type BoardFilterResult,
  type BoardFilterTarget,
  boardFilterResultFromInterpretation,
  parseBoardFilterRequest,
  viewerPeriodAfter,
} from './naturalLanguageBoardFilters';
import { useDebouncedValue } from './useDebouncedValue';

export type FilterSource = {
  cardId: string;
  title: string;
  rows: Array<Record<string, unknown>>;
  columns: Array<string>;
};

type DatasetFilterBarProps = {
  savedFilters?: React.ReactNode;
  lensId?: string;
  queryFilters?: LensFilterValues;
  /** Declared board filters are rendered by the parent but share this natural-language control. */
  declaredFilters?: Array<LensFilter>;
  declaredFilterValues?: LensFilterValues;
  declaredFilterOptions?: Record<string, Array<string>>;
  onChangeDeclaredFilters: (values: LensFilterValues) => void;
  /** Every card that returned rows: filtering needs a shared field, not a shared query. */
  sources: Array<FilterSource>;
  /** Successful card reads, including empty results, independently of option rows. */
  coverageCardCount?: number;
  /** Dataset dimensions still seed the default, where consolidation set any. */
  datasets: Array<LensDataset>;
  /** Columns chosen for the dashboard. Absent means offer a guess. */
  filterColumns?: Array<string>;
  onSetFilterColumns: (columns: Array<string>) => void;
  /** Set while the Lens's Configure menu has opened the column picker. */
  columnPickerAnchor?: HTMLElement | null;
  onCloseColumnPicker?: () => void;
  /** Card titles a column reaches, keyed by column. */
  affectedCards: Record<string, Array<string>>;
  selections: LensSelection;
  onChange: (selection: LensSelection) => void;
  /** Measure ranges, keyed by column like the value selections. */
  ranges: Record<string, LensRange>;
  onChangeRanges: (ranges: Record<string, LensRange>) => void;
  /**
   * The dashboard period is committed explicitly and bounds every dataset read.
   */
  /** The window and comparison in force: the board's, with the viewer's own keys over it. */
  period: LensPeriod;
  boardPeriod: LensPeriod;
  resolvedPeriod?: LensResolvedPeriod;
  onChangePeriod: (patch: Partial<LensPeriod> | null) => void;
  onSaveBoardPeriod: (period: LensPeriod) => Promise<void>;
  calendar?: LensCalendar;
  /** Datasets the period reaches, by name. Empty means nothing is date-bound. */
  datedDatasets: Array<string>;
  /** True while the new period's queries are still running. */
  isReloading?: boolean;
  onClearAll: () => void;
  canEdit: boolean;
};

/**
 * Select defaults to the selectedMenu variant, which anchors the popup on the
 * chosen item and so centres it over the control. Anchoring below the box keeps
 * the list where a reader expects it, and a capped height makes a long one
 * scroll rather than run off the page.
 */
const DROPDOWN_PROPS = {
  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  transformOrigin: { horizontal: 'left', vertical: 'top' },
  variant: 'menu',
  PaperProps: { sx: { maxHeight: 360 } },
} as const;

/**
 * Dataset selections reach the server before aggregation. Options start from
 * loaded rows and fetch the complete bounded dimension list when opened.
 *
 * Controls are keyed by column name across datasets: three datasets carrying
 * "vendor" is one filter to a reader, and picking a vendor moves all of them.
 *
 * The bar sits directly on the canvas as a single compact row with a bottom
 * hairline, not inside a filled panel: a filter row is a toolbar, not its own
 * screen band.
 */
export default function DatasetFilterBar({
  savedFilters,
  lensId,
  queryFilters = {},
  declaredFilters = [],
  declaredFilterValues = {},
  declaredFilterOptions = {},
  onChangeDeclaredFilters,
  sources,
  coverageCardCount,
  datasets,
  filterColumns,
  columnPickerAnchor,
  onCloseColumnPicker,
  onSetFilterColumns,
  affectedCards,
  selections,
  onChange,
  ranges,
  onChangeRanges,
  period,
  boardPeriod,
  resolvedPeriod,
  onChangePeriod,
  calendar,
  onSaveBoardPeriod,
  datedDatasets,
  isReloading,
  onClearAll,
  canEdit,
}: DatasetFilterBarProps) {
  // Opened from the Lens's Configure menu rather than from a control beside
  // the filters: which columns are offered is a decision about the Lens, not
  // one a reader makes while reading it.
  const [picking, setPicking] = useState<{ anchor: HTMLElement } | null>(null);
  // Search text per column: a vendor list runs to hundreds of values, and
  // scrolling one is not a way to find anything. The typed value updates
  // immediately so the field feels responsive; only the debounced value below
  // drives the (potentially large) options scan, so a keystroke does not
  // rescan every loaded row of every card.
  const [search, setSearch] = useState<Record<string, string>>({});
  const [fieldSearch, setFieldSearch] = useState('');
  // Which filter's menu is open, and which one is showing its tooltip. The
  // tooltip wraps the whole control, so without this the pointer that opened
  // the menu is still counted as hovering and the hint sits over the options
  // the user opened the menu to read.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hintFor, setHintFor] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 150);
  const governedDimensions = useMemo(
    () => [
      ...new Set(
        datasets
          .filter((dataset) => dataset.source?.kind === 'governed')
          .flatMap(
            (dataset) =>
              dataset.columns?.filter((field) => field.kind === 'dimension').map((field) => field.name) ?? [],
          ),
      ),
    ],
    [datasets],
  );
  const optionDataset = datasets.find(
    (dataset) =>
      dataset.source?.kind === 'governed' &&
      dataset.columns?.some((field) => field.kind === 'dimension' && field.name === openMenu),
  );
  const optionQuery = useGetLensColumnValues({
    lensId,
    datasetId: optionDataset?.id,
    column: openMenu ?? undefined,
    filters: queryFilters,
  });

  // A dataset's own declared label wins; failing that the column name is
  // humanised, so "product bk" reads as "Product" everywhere the column
  // appears: the dropdown label, its search placeholder and the picker menu.
  const displayMetadata = useGovernedDisplayMetadata();
  const displayDatasets = datasets.map((dataset) => governedDisplayDataset(dataset, displayMetadata)!);
  const label = (column: string) => humaniseFilterLabel(column, declaredDatasetLabel(column, displayDatasets));
  const description = (column: string) => declaredDatasetDescription(column, displayDatasets);

  // Default: what the results suggest, plus anything consolidation named.
  // Memoised on the props that actually determine it: typing in the search
  // box above sets local `search` state, which re-renders this component,
  // and without this these scans over every loaded row of every card would
  // otherwise redo on every keystroke rather than only when the underlying
  // data or column choice changes.
  const suggested = useMemo(
    () =>
      [
        ...new Set([
          ...sources.flatMap((source) => inferDimensions(source.rows, source.columns)),
          ...datasets.flatMap((dataset) => dataset.dimensions ?? []),
        ]),
      ].filter((column) => sources.some((source) => source.columns.includes(column))),
    [sources, datasets],
  );

  // An active selection must remain visible even when the narrowed backend
  // response no longer returns that filter-only field. Otherwise applying a
  // filter makes its own control disappear and leaves Clear all as the only
  // route back.
  const chosenColumns = useMemo(
    () => [...new Set([...(filterColumns ?? suggested), ...Object.keys(selections)])],
    [filterColumns, selections, suggested],
  );

  // Every column any card carries, for the picker.
  const availableColumns = useMemo(
    () => [...new Set([...sources.flatMap((source) => source.columns), ...governedDimensions])].sort(),
    [sources, governedDimensions],
  );

  // One control per column name, pooling values from every card that knows it.
  const columns = useMemo(() => {
    const byColumn = new Map<string, Set<string>>();
    for (const source of sources) {
      for (const column of chosenColumns) {
        if (!source.columns.includes(column)) {
          continue;
        }
        if (looksLikeDate(source.rows, column) || numericColumns(source.rows, [column]).length) {
          continue;
        }
        const existing = byColumn.get(column) ?? new Set<string>();
        distinctValues(source.rows, column).forEach((value) => existing.add(value));
        byColumn.set(column, existing);
      }
    }
    for (const column of chosenColumns) {
      if (!governedDimensions.includes(column)) continue;
      const values = byColumn.get(column) ?? new Set<string>();
      (selections[column] ?? []).forEach((value) => values.add(value));
      if (openMenu === column) optionQuery.data?.values.forEach((value) => values.add(value));
      byColumn.set(column, values);
    }
    // Workspace and older governed datasets can omit a dimensions contract.
    // Preserve the selected values for those too, so their active control does
    // not vanish after the result is narrowed.
    for (const [column, selected] of Object.entries(selections)) {
      if (!chosenColumns.includes(column)) continue;
      const values = byColumn.get(column) ?? new Set<string>();
      selected.forEach((value) => values.add(value));
      byColumn.set(column, values);
    }
    return [...byColumn.entries()].map(([column, values]) => ({
      column,
      options: [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    }));
  }, [sources, chosenColumns, governedDimensions, selections, openMenu, optionQuery.data]);

  // Natural-language matching may use a dimension carried by a card even
  // when that field is not currently shown in the drawer. This makes
  // "Japan only" useful when a destination breakdown is already on the Lens,
  // while fields with no loaded values remain an explicit Add filter choice.
  const naturalLanguageColumns = useMemo(() => {
    const byColumn = new Map<string, Set<string>>();
    for (const source of sources) {
      for (const column of availableColumns) {
        if (!source.columns.includes(column)) continue;
        if (looksLikeDate(source.rows, column) || numericColumns(source.rows, [column]).length) continue;
        const values = byColumn.get(column) ?? new Set<string>();
        distinctValues(source.rows, column).forEach((value) => values.add(value));
        byColumn.set(column, values);
      }
    }
    return [...byColumn.entries()].map(([column, values]) => ({
      column,
      options: [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    }));
  }, [availableColumns, sources]);

  // Measures are narrowed by range rather than by picking values, and their
  // span comes from the rows already loaded.
  const measures = useMemo(() => {
    const result = new Map<string, LensRange>();
    for (const source of sources) {
      // Measures stay opt-in: only a column somebody chose gets a range.
      for (const column of chosenColumns.filter(
        (candidate) => source.columns.includes(candidate) && numericColumns(source.rows, [candidate]).length > 0,
      )) {
        const extent = valueExtent(source.rows, column);
        const existing = result.get(column);
        result.set(column, {
          min: existing?.min === undefined ? extent.min : Math.min(existing.min, extent.min ?? existing.min),
          max: existing?.max === undefined ? extent.max : Math.max(existing.max, extent.max ?? existing.max),
        });
      }
    }
    return result;
  }, [sources, chosenColumns]);

  const totalCards = coverageCardCount ?? sources.length;

  const handleClearAll = () => {
    onClearAll();
    announceLensFilter(describeClearAllAnnouncement(totalCards));
  };

  const naturalLanguageTargets = useMemo<Array<BoardFilterTarget>>(
    () => [
      ...declaredFilters
        .filter((filter) => filter.type === 'select')
        .map((filter) => ({
          key: `declared:${filter.id}`,
          label: filter.label,
          multiple: Boolean(filter.multiple),
          visible: true,
          options: Array.from(
            new Set([
              ...(declaredFilterOptions[filter.id]?.length ? declaredFilterOptions[filter.id] : (filter.options ?? [])),
              ...(Array.isArray(declaredFilterValues[filter.id])
                ? (declaredFilterValues[filter.id] as Array<string>)
                : typeof declaredFilterValues[filter.id] === 'string' && declaredFilterValues[filter.id]
                  ? [declaredFilterValues[filter.id] as string]
                  : []),
            ]),
          ),
        })),
      ...naturalLanguageColumns.map(({ column, options }) => ({
        key: `selection:${column}`,
        label: label(column),
        multiple: true,
        options,
        visible: chosenColumns.includes(column),
      })),
    ],
    [chosenColumns, declaredFilterOptions, declaredFilterValues, declaredFilters, naturalLanguageColumns],
  );

  const hasDatedCards = datedDatasets.length > 0;

  const applyNaturalLanguageProposal = (proposal: BoardFilterProposal) => {
    const nextDeclared = { ...declaredFilterValues };
    let declaredFiltersChanged = false;

    // The reporting period and declared filters live in the same parent state.
    // Commit them together so a multi-part request cannot overwrite its own
    // period change with a subsequent declared-filter update.
    if (proposal.period !== undefined && hasDatedCards) {
      const next = viewerPeriodAfter(
        declaredFilterValues[LENS_PERIOD_KEY] as Partial<LensPeriod> | undefined,
        proposal.period,
      );
      if (next) {
        nextDeclared[LENS_PERIOD_KEY] = next as LensPeriod;
      } else {
        delete nextDeclared[LENS_PERIOD_KEY];
      }
      declaredFiltersChanged = true;
    }

    if (proposal.clearValues) {
      onClearAll();
      const declaredIds = new Set(
        declaredFilters.filter((filter) => filter.type === 'select').map((filter) => filter.id),
      );
      Object.keys(nextDeclared).forEach((key) => {
        if (declaredIds.has(key)) delete nextDeclared[key];
      });
      declaredFiltersChanged = true;
    }

    if (proposal.values) {
      const nextSelections = proposal.clearValues ? {} : { ...selections };
      for (const [key, values] of Object.entries(proposal.values)) {
        if (key.startsWith('selection:')) {
          nextSelections[key.slice('selection:'.length)] = values ?? [];
          continue;
        }
        if (key.startsWith('declared:')) {
          const filterId = key.slice('declared:'.length);
          const filter = declaredFilters.find((candidate) => candidate.id === filterId);
          nextDeclared[filterId] = filter?.multiple ? (values ?? []) : (values?.[0] ?? '');
          declaredFiltersChanged = true;
        }
      }
      onChange(nextSelections);
    }

    if (declaredFiltersChanged) onChangeDeclaredFilters(nextDeclared);

    announceLensFilter(`Applied board filters: ${proposal.changes.join(', ')}`);
  };

  // An editor can configure dimensions elsewhere, but an empty filter shell
  // does not help anyone understand or use the dashboard.
  if (!savedFilters && !columns.length && !measures.size && !hasDatedCards && !availableColumns.length) {
    return (
      <Typography color="text.secondary" variant="body2">
        These cards use fixed query results. To change their dates or filters, open a card’s Source tab or ask
        askRichard for a new view.
      </Typography>
    );
  }

  return (
    <>
      <Stack spacing={2} sx={{ minWidth: 0, width: '100%' }}>
        <NaturalLanguageBoardFilter
          allowPeriod={hasDatedCards}
          onApply={applyNaturalLanguageProposal}
          targets={naturalLanguageTargets}
        />

        {hasDatedCards && (
          <PeriodRows
            appliesTo={datedDatasets}
            boardPeriod={boardPeriod}
            calendar={calendar}
            canEdit={canEdit}
            isReloading={isReloading}
            onChange={onChangePeriod}
            onSaveBoard={onSaveBoardPeriod}
            period={period}
            resolved={resolvedPeriod}
          />
        )}

        <Divider />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle2">Filter values</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
              Changes apply to linked cards.
            </Typography>
          </Box>
          {canEdit && availableColumns.length > 0 && (
            <Button
              onClick={(event) => {
                setFieldSearch('');
                setPicking({ anchor: event.currentTarget });
              }}
              size="small"
              variant="outlined"
            >
              Add filter
            </Button>
          )}
        </Stack>
        {savedFilters}
        {!columns.length && !measures.size && !savedFilters && (
          <Typography color="text.secondary" variant="body2">
            No filters yet. Add a field to narrow your results.
          </Typography>
        )}
        {columns.map(({ column, options }) => {
          // Absent means every value: the control opens fully ticked.
          const chosen = selections[column];
          const allTicked = chosen === undefined;
          const cards = affectedCards[column] ?? [];
          // Debounced so a fast typist does not rescan the option list once
          // per keystroke; the field itself still echoes every keystroke.
          const term = (debouncedSearch[column] ?? '').trim().toLowerCase();
          const visible = term ? options.filter((option) => option.toLowerCase().includes(term)) : options;

          return (
            <Tooltip
              key={column}
              onClose={() => setHintFor(null)}
              onOpen={() => setHintFor(column)}
              open={hintFor === column && openMenu !== column}
              title={cards.length > 0 ? `Affects ${cards.join(', ')}` : ''}
            >
              <FormControl
                size="small"
                sx={{
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <InputLabel id={`le-lens-filter-${column}`}>{label(column)}</InputLabel>
                <Select
                  MenuProps={DROPDOWN_PROPS}
                  label={label(column)}
                  labelId={`le-lens-filter-${column}`}
                  multiple
                  onChange={() => {
                    /* Every change is handled on the items, so the tick state
                       can distinguish all from none. */
                  }}
                  onClose={() => setOpenMenu(null)}
                  onOpen={() => setOpenMenu(column)}
                  renderValue={() => (chosen === undefined ? 'All' : chosen.length === 0 ? 'None' : chosen.join(', '))}
                  // Absent means everything, so show every option ticked.
                  value={chosen ?? options}
                >
                  {options.length > 8 && (
                    // Select swallows typing for its own jump-to-letter, so the
                    // field has to keep its keystrokes to itself.
                    <Box
                      onKeyDown={(event) => {
                        if (event.key !== 'Escape') event.stopPropagation();
                      }}
                      sx={{ px: 1.5, py: 0.5 }}
                    >
                      <TextField
                        autoFocus
                        fullWidth
                        onChange={(event) => setSearch((current) => ({ ...current, [column]: event.target.value }))}
                        onClick={(event) => event.stopPropagation()}
                        placeholder={`Search ${label(column)}`}
                        size="small"
                        value={search[column] ?? ''}
                      />
                    </Box>
                  )}
                  <MenuItem
                    onClick={(event) => {
                      event.preventDefault();
                      const updated = { ...selections };
                      // With a search running, select-all means these matches,
                      // so searching then selecting is a way to pick a group.
                      if (term) {
                        const kept = new Set([...(chosen ?? options), ...visible]);
                        updated[column] = options.filter((option) => kept.has(option));
                      } else if (allTicked) {
                        // Ticked means all: unticking it selects nothing, which
                        // shows nothing, rather than quietly meaning everything.
                        updated[column] = [];
                      } else {
                        delete updated[column];
                      }
                      onChange(updated);
                      announceLensFilter(describeSelectionAnnouncement(label(column), updated[column], cards.length));
                    }}
                    value="__all"
                  >
                    <Checkbox
                      checked={allTicked}
                      indeterminate={!allTicked && (chosen?.length ?? 0) > 0}
                      size="small"
                    />
                    <ListItemText primary={term ? `Add these ${visible.length}` : 'Select all'} />
                  </MenuItem>
                  <Divider />
                  {visible.map((option) => {
                    const ticked = (chosen ?? options).includes(option);
                    return (
                      <MenuItem
                        key={option}
                        onClick={(event) => {
                          event.preventDefault();
                          const from = chosen ?? options;
                          const next = ticked ? from.filter((entry) => entry !== option) : [...from, option];
                          const updated = { ...selections };
                          if (next.length === options.length) {
                            delete updated[column];
                          } else {
                            updated[column] = next;
                          }
                          onChange(updated);
                          announceLensFilter(
                            describeSelectionAnnouncement(label(column), updated[column], cards.length),
                          );
                        }}
                        value={option}
                      >
                        <Checkbox checked={ticked} size="small" />
                        <ListItemText primary={option} />
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            onChange({ ...selections, [column]: [option] });
                            announceLensFilter(describeSelectionAnnouncement(label(column), [option], cards.length));
                          }}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Only
                        </Button>
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', gap: 0.5, lineHeight: 1.35, ml: 0 }}
                >
                  {cards.length} of {totalCards} cards
                  {description(column) ? (
                    <Tooltip title={description(column)}>
                      <InfoOutlinedIcon aria-label={`About ${label(column)}`} sx={{ fontSize: 15 }} />
                    </Tooltip>
                  ) : null}
                </FormHelperText>
              </FormControl>
            </Tooltip>
          );
        })}

        {[...measures.entries()].map(([column, extent]) => (
          <MeasureRangeControl
            cards={affectedCards[column] ?? []}
            extent={extent}
            key={column}
            label={label(column)}
            onChangeCommitted={(patch) => {
              const updated = { ...ranges };
              if (patch.min === undefined && patch.max === undefined) {
                delete updated[column];
              } else {
                updated[column] = patch;
              }
              onChangeRanges(updated);
            }}
            range={ranges[column] ?? {}}
          />
        ))}

        {(hasSelection(selections) || Object.keys(ranges).length > 0) && (
          <Chip
            label="Clear filters"
            onClick={handleClearAll}
            onDelete={handleClearAll}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <Menu
        anchorEl={picking?.anchor ?? columnPickerAnchor ?? null}
        onClose={() => {
          setPicking(null);
          onCloseColumnPicker?.();
        }}
        open={Boolean(picking || columnPickerAnchor)}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 440 } } }}
      >
        <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Find a field…"
            inputProps={{ 'aria-label': 'Find a filter field' }}
            value={fieldSearch}
            onChange={(event) => setFieldSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') event.stopPropagation();
            }}
          />
        </Box>
        {!availableColumns.some((column) =>
          `${label(column)} ${column}`.toLowerCase().includes(fieldSearch.toLowerCase()),
        ) && <MenuItem disabled>No matching fields</MenuItem>}
        {availableColumns
          .filter((column) => `${label(column)} ${column}`.toLowerCase().includes(fieldSearch.toLowerCase()))
          .map((column) => {
            const carrier = sources.find((source) => source.columns.includes(column));
            const rows = carrier?.rows ?? [];
            const isShown = chosenColumns.includes(column);
            // Say what ticking a column will get you, so a date does not look
            // like a control that failed to appear.
            const isDate = looksLikeDate(rows, column);
            const isMeasure = !isDate && numericColumns(rows, [column]).length > 0;

            return (
              <MenuItem
                disabled={isDate}
                key={column}
                onClick={() =>
                  onSetFilterColumns(
                    isShown ? chosenColumns.filter((name) => name !== column) : [...chosenColumns, column],
                  )
                }
              >
                <Checkbox checked={isShown && !isDate} disabled={isDate} size="small" />
                <ListItemText
                  primary={label(column)}
                  secondary={
                    isDate
                      ? 'Use the reporting period above'
                      : isMeasure
                        ? 'Filter displayed values'
                        : `${affectedCards[column]?.length ?? 0} of ${totalCards} cards`
                  }
                />
              </MenuItem>
            );
          })}
        <Divider />
        <MenuItem
          onClick={() => {
            onSetFilterColumns([]);
            setPicking(null);
          }}
        >
          <ListItemText primary="Remove all fields" />
        </MenuItem>
      </Menu>
    </>
  );
}

/** A short, filter-only conversation. The board changes only after Apply. */
type FilterConversationTurn = {
  id: number;
  request: string;
  response?: BoardFilterResult;
  status?: 'applied' | 'discarded' | 'revised';
  usedBasicMatch?: boolean;
};

function revisedFilterProposal(current: BoardFilterProposal | null, change: BoardFilterProposal): BoardFilterProposal {
  if (!current) {
    return change;
  }
  const changes = new Map<string, string>();
  for (const label of current.changes) {
    changes.set(label.split(':')[0], label);
  }
  if (change.clearValues) {
    for (const label of current.changes) {
      if (!label.startsWith('Period:') && !label.startsWith('Comparison:')) {
        changes.delete(label.split(':')[0]);
      }
    }
  }
  for (const label of change.changes) {
    changes.set(label.split(':')[0], label);
  }
  return {
    kind: 'ready',
    changes: [...changes.values()],
    ...(current.period || change.period ? { period: { ...current.period, ...change.period } } : {}),
    ...(current.values || change.values
      ? { values: { ...(change.clearValues ? {} : current.values), ...change.values } }
      : {}),
    ...(current.clearValues || change.clearValues ? { clearValues: true } : {}),
  };
}

function NaturalLanguageBoardFilter({
  targets,
  allowPeriod = true,
  onApply,
}: {
  targets: ReadonlyArray<BoardFilterTarget>;
  allowPeriod?: boolean;
  onApply: (proposal: BoardFilterProposal) => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [query, setQuery] = useState('');
  const [turns, setTurns] = useState<Array<FilterConversationTurn>>([]);
  const [pending, setPending] = useState<{ turnId: number; proposal: BoardFilterProposal } | null>(null);
  const nextTurnId = useRef(0);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const interpretation = useInterpretLensFilters();
  const trimmed = query.trim();
  const placeholder = turns.length ? 'What else would you like to change?' : 'Try a date, comparison or value…';

  useEffect(() => {
    const conversation = conversationRef.current;
    if (conversation) {
      conversation.scrollTop = conversation.scrollHeight;
    }
  }, [turns, interpretation.isPending]);

  const send = async () => {
    if (!trimmed) {
      return;
    }
    // Each message changes the pending preview, without losing earlier choices.
    // Once applied, the next message changes the Lens's existing controls.
    const turnId = ++nextTurnId.current;
    setTurns((current) => [...current, { id: turnId, request: trimmed }]);
    setQuery('');
    try {
      const serverResponse = await interpretation.mutateAsync({ query: trimmed, targets, allowPeriod });
      const interpreted = boardFilterResultFromInterpretation(serverResponse, allowPeriod);
      const usedBasicMatch = serverResponse.kind === 'error' && serverResponse.reason === 'model_unavailable';
      const basicMatch = usedBasicMatch ? parseBoardFilterRequest(trimmed, targets, allowPeriod) : interpreted;
      const response =
        basicMatch.kind === 'ready' ? revisedFilterProposal(pending?.proposal ?? null, basicMatch) : interpreted;
      setTurns((current) =>
        current.map((turn) =>
          turn.id === turnId
            ? { ...turn, response, usedBasicMatch: usedBasicMatch && response.kind === 'ready' }
            : turn,
        ),
      );
      if (response.kind === 'ready') {
        setTurns((current) =>
          current.map((turn) => (turn.id === pending?.turnId ? { ...turn, status: 'revised' } : turn)),
        );
        setPending({ turnId, proposal: response });
      }
    } catch (error) {
      setTurns((current) =>
        current.map((turn) =>
          turn.id === turnId
            ? {
                ...turn,
                response: {
                  kind: 'error',
                  message:
                    error instanceof Error
                      ? error.message
                      : 'askRichard could not interpret filters just now. Try again.',
                },
              }
            : turn,
        ),
      );
    }
  };

  const apply = () => {
    if (!pending) {
      return;
    }
    onApply(pending.proposal);
    setTurns((current) => current.map((turn) => (turn.id === pending.turnId ? { ...turn, status: 'applied' } : turn)));
    setPending(null);
    inputRef.current?.focus();
  };

  const discard = () => {
    if (!pending) {
      return;
    }
    setTurns((current) =>
      current.map((turn) => (turn.id === pending.turnId ? { ...turn, status: 'discarded' } : turn)),
    );
    setPending(null);
    inputRef.current?.focus();
  };

  return (
    <Box
      component="section"
      aria-label="Filter with askRichard"
      sx={{
        bgcolor: tokens.surface,
        border: `1px solid ${alpha(tokens.accent, 0.2)}`,
        borderRadius: 1.5,
        p: 1.25,
        '&:focus-within': { borderColor: tokens.accent },
      }}
    >
      <Stack alignItems="center" direction="row" spacing={0.75} sx={{ mb: 1 }}>
        <RichardMonogram inverted={theme.palette.mode === 'dark'} sx={{ fontSize: 20 }} />
        <Typography
          component="h3"
          sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 14, fontWeight: 700 }}
        >
          Filter with askRichard
        </Typography>
      </Stack>
      {turns.length > 0 && (
        <Stack
          ref={conversationRef}
          aria-label="Filter conversation"
          aria-live="polite"
          spacing={2}
          sx={{ maxHeight: 320, mb: 1.5, overflowY: 'auto', pr: 0.5 }}
        >
          {turns.map((turn) => (
            <Stack key={turn.id} spacing={0.75}>
              <Box
                sx={{
                  alignSelf: 'flex-end',
                  bgcolor: alpha(tokens.accent, 0.08),
                  borderRadius: 1.5,
                  maxWidth: '88%',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography sx={{ fontSize: 13.5, overflowWrap: 'anywhere' }}>{turn.request}</Typography>
              </Box>
              <Stack alignItems="flex-start" direction="row" spacing={0.75}>
                <RichardMonogram inverted={theme.palette.mode === 'dark'} sx={{ flexShrink: 0, fontSize: 24 }} />
                <Box sx={{ bgcolor: alpha(tokens.accent, 0.04), borderRadius: 1.5, minWidth: 0, px: 1.25, py: 1 }}>
                  {!turn.response ? (
                    <Stack alignItems="center" direction="row" spacing={0.75}>
                      <CircularProgress size={13} />
                      <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                        Checking this Lens’s controls…
                      </Typography>
                    </Stack>
                  ) : turn.response.kind === 'error' ? (
                    <Typography color="text.secondary" role="alert" sx={{ fontSize: 12.5 }}>
                      {turn.response.message}
                    </Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      <Typography sx={{ fontSize: 12.5 }}>
                        {turn.status === 'applied'
                          ? 'Applied to this view.'
                          : turn.status === 'discarded'
                            ? 'Preview discarded.'
                            : turn.status === 'revised'
                              ? 'Revised below.'
                              : 'Proposed:'}
                      </Typography>
                      {turn.status !== 'revised' && turn.status !== 'discarded' && (
                        <Stack spacing={0.4} sx={{ pl: 0.25 }}>
                          {turn.response.changes.map((change) => {
                            const separator = change.indexOf(':');
                            return (
                              <Stack direction="row" key={change} spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                {separator > -1 ? (
                                  <>
                                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                                      {change.slice(0, separator)}:
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                      {change.slice(separator + 1).trim()}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography sx={{ fontSize: 12 }}>{change}</Typography>
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                      {turn.usedBasicMatch && pending?.turnId === turn.id && (
                        <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
                          askRichard is unavailable, so this is a basic match. Check it before applying.
                        </Typography>
                      )}
                      {pending?.turnId === turn.id && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          <Button
                            disabled={interpretation.isPending}
                            onClick={apply}
                            size="small"
                            startIcon={<CheckIcon />}
                            variant="contained"
                          >
                            Apply filters
                          </Button>
                          <Button disabled={interpretation.isPending} onClick={discard} size="small">
                            Discard
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      {turns.length > 0 && <Divider sx={{ mb: 1.5 }} />}
      <Stack alignItems="center" direction="row" spacing={1}>
        <TextField
          fullWidth
          id="lens-filter-command"
          inputProps={{ 'aria-label': 'Ask askRichard to filter', maxLength: 500 }}
          inputRef={inputRef}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (!interpretation.isPending) void send();
            }
          }}
          placeholder={placeholder}
          size="small"
          value={query}
          sx={{
            minWidth: 0,
            '& .MuiOutlinedInput-root': { borderRadius: 1 },
            '& .MuiInputBase-input': { fontSize: 13.5 },
          }}
        />
        <Button
          disabled={!trimmed || interpretation.isPending}
          onClick={() => void send()}
          size="small"
          variant="contained"
          sx={{ flexShrink: 0, minHeight: 40, minWidth: 58 }}
        >
          Send
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Comparison is deliberately an opt-in analytical lens, not a global display
 * toggle. It adds a quiet delta beneath KPI values while leaving the current
 * period as the dominant reading of every chart and table.
 */
function MeasureRangeControl({
  label: fieldLabel,
  range,
  extent,
  cards,
  onChangeCommitted,
}: {
  label: string;
  range: LensRange;
  extent: LensRange;
  cards: Array<string>;
  onChangeCommitted: (patch: LensRange) => void;
}) {
  const [minText, setMinText] = useState(range.min === undefined ? '' : String(range.min));
  const [maxText, setMaxText] = useState(range.max === undefined ? '' : String(range.max));

  // Follow a range cleared elsewhere, such as "Clear filters", without
  // fighting a value still being typed here.
  useEffect(() => {
    setMinText(range.min === undefined ? '' : String(range.min));
  }, [range.min]);
  useEffect(() => {
    setMaxText(range.max === undefined ? '' : String(range.max));
  }, [range.max]);

  const debouncedMin = useDebouncedValue(minText, 400);
  const debouncedMax = useDebouncedValue(maxText, 400);

  useEffect(() => {
    const parsedMin = debouncedMin.trim() === '' ? undefined : Number(debouncedMin);
    const parsedMax = debouncedMax.trim() === '' ? undefined : Number(debouncedMax);
    if (parsedMin === range.min && parsedMax === range.max) {
      return;
    }
    onChangeCommitted({ min: parsedMin, max: parsedMax });
    announceLensFilter(describeRangeAnnouncement(fieldLabel, { min: parsedMin, max: parsedMax }, cards.length));
    // Committing intentionally reacts only to the settled, debounced text;
    // range/cards/onChangeCommitted are read for comparison, not to retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMin, debouncedMax]);

  return (
    <Tooltip title={cards.length > 0 ? `Affects ${cards.join(', ')}` : ''}>
      <Box>
        {/* The measure is named once above, so neither input has to carry a
            label long enough to be truncated. */}
        <Typography sx={{ display: 'block', mb: 0.25, ml: 0.5 }} variant="caption">
          {fieldLabel}
        </Typography>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <TextField
            aria-label={`${fieldLabel} from`}
            onChange={(event) => setMinText(event.target.value)}
            placeholder={extent.min === undefined ? 'min' : String(Math.floor(extent.min))}
            size="small"
            sx={{ width: 96 }}
            type="number"
            value={minText}
          />
          <Typography color="text.secondary" variant="caption">
            to
          </Typography>
          <TextField
            aria-label={`${fieldLabel} to`}
            onChange={(event) => setMaxText(event.target.value)}
            placeholder={extent.max === undefined ? 'max' : String(Math.ceil(extent.max))}
            size="small"
            sx={{ width: 96 }}
            type="number"
            value={maxText}
          />
        </Stack>
      </Box>
    </Tooltip>
  );
}

/**
 * The dashboard period.
 *
 * Every other control in this bar narrows rows already in the browser, which is
 * free and so can be live. A period cannot work that way: asking for March
 * when April was loaded needs rows nobody fetched. So this one is a draft the
 * reader commits, and a calendar range that fired on each half would bill a
 * period nobody asked to see.
 *
 * Everyday periods are direct, labelled choices. The calendar only appears
 * after choosing Custom, so readers do not have to understand a saved
 * "default" before they can ask for the last seven days.
 */
